import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Heart, Shield, Package, Users, ExternalLink } from "lucide-react";

const impactLevels = [
  {
    amount: "$10",
    description: "helps provide underwear to 1 worker, no strings attached.",
  },
  {
    amount: "$100",
    description: "helps support ~5-10 workers (shipping and sizing vary).",
  },
  {
    amount: "$500",
    description:
      "funds a full outreach batch and strengthens SWC's capacity to respond to worker-identified needs.",
  },
];

const fundingUses = [
  "Underwear procurement",
  "Discreet packaging and shipping",
  "Fulfillment coordination",
  "Staff time to respond to worker-identified needs",
  "Anonymized insight that informs Safe Work Coalition advocacy and resources",
];

export default function Donate() {
  return (
    <div className="bg-[color:var(--pws-off)] min-h-[calc(100vh-80px)]">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20">
        <div className="container-pws">
          <div className="max-w-3xl">
            <h1
              className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[color:var(--pws-charcoal)] tracking-tight"
              data-testid="donate-title"
            >
              Panties Without Strings
            </h1>
            <p className="mt-4 text-xl text-[color:var(--pws-ink)] font-medium" data-testid="donate-subtitle">
              Support that comes without conditions.
            </p>
            <p className="mt-4 text-[color:var(--pws-stone)]">
              Panties Without Strings is a Safe Work Coalition campaign providing
              no-strings support to adult industry workers—starting with free, clean
              underwear and extending to resources, care, and advocacy.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="container-pws">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Why & What */}
            <div className="lg:col-span-2 space-y-8">
              {/* Why This Exists */}
              <Card className="border-[color:var(--pws-line)] bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)] mb-4">
                    Why this exists
                  </h2>
                  <p className="text-[color:var(--pws-ink)]">
                    Many adult industry workers avoid traditional nonprofits because
                    "help" often comes with judgment, surveillance, data extraction, or
                    pressure to change their work. Even when resources exist, the trust
                    barrier blocks access.
                  </p>
                </CardContent>
              </Card>

              {/* Our Approach */}
              <Card className="border-[color:var(--pws-line)] bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)] mb-4">
                    Our approach
                  </h2>
                  <p className="text-[color:var(--pws-ink)]">
                    We start by offering something basic and unconditional—free
                    underwear, no conditions—and then we listen. This trust-first
                    approach helps Safe Work Coalition understand what workers actually
                    need and advocate more effectively.
                  </p>
                </CardContent>
              </Card>

              {/* Why Underwear */}
              <Card className="border-[color:var(--pws-line)] bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)] mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-[color:var(--pws-plum)]" />
                    Why underwear
                  </h2>
                  <p className="text-[color:var(--pws-ink)]">
                    Underwear is a practical necessity. It's personal, often overlooked,
                    and sometimes hard to prioritize under stress. Offering it freely is
                    a simple, dignified way to provide support without coercion.
                  </p>
                </CardContent>
              </Card>

              {/* Impact */}
              <Card className="border-[color:var(--pws-line)] bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)] mb-4 flex items-center gap-2">
                    <Heart className="h-5 w-5 text-[color:var(--pws-plum)]" />
                    Your impact
                  </h2>
                  <div className="space-y-4">
                    {impactLevels.map((level, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <span className="font-semibold text-[color:var(--pws-plum)] min-w-[60px]">
                          {level.amount}
                        </span>
                        <span className="text-[color:var(--pws-ink)]">
                          {level.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* What Your Donation Funds */}
              <Card className="border-[color:var(--pws-line)] bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)] mb-4">
                    What your donation funds
                  </h2>
                  <ul className="space-y-2">
                    {fundingUses.map((use, index) => (
                      <li key={index} className="flex items-start gap-2 text-[color:var(--pws-ink)]">
                        <span className="text-[color:var(--pws-plum)]">•</span>
                        {use}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* What We Don't Do */}
              <Card className="border-[color:var(--pws-line)] bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)] mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-[color:var(--pws-plum)]" />
                    What we don't do
                  </h2>
                  <p className="text-[color:var(--pws-ink)]">
                    We don't sell data. We don't share data with platforms or law
                    enforcement. We don't require legal names or proof of work. Workers
                    can take the underwear and never hear from us again.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Donate CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="border-[color:var(--pws-plum)] bg-white shadow-lg">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <Users className="h-12 w-12 mx-auto text-[color:var(--pws-plum)]" />
                      <h3 className="mt-4 font-['Space_Grotesk'] text-xl font-semibold text-[color:var(--pws-charcoal)]">
                        Support Workers
                      </h3>
                      <p className="mt-2 text-sm text-[color:var(--pws-stone)]">
                        Your donation provides unconditional support to adult industry
                        workers.
                      </p>
                      <Separator className="my-6 bg-[color:var(--pws-line)]" />
                      <Button
                        asChild
                        size="lg"
                        className="w-full bg-[color:var(--pws-plum)] hover:bg-[color:var(--pws-plum-hover)] text-white btn-hover"
                        data-testid="donate-cta-btn"
                      >
                        <a
                          href="https://safeworkcoalition.org/donate"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Donate to Support PWS
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                      <p className="mt-4 text-xs text-[color:var(--pws-stone)]">
                        Donations go through Safe Work Coalition's secure payment
                        processor.
                      </p>
                    </div>

                    {/* Trust badges */}
                    <div className="mt-6 pt-6 border-t border-[color:var(--pws-line)]">
                      <div className="flex flex-wrap gap-2 justify-center">
                        <span className="trust-badge text-xs">No tracking pixels</span>
                        <span className="trust-badge text-xs">Secure donation</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* FAQ Link */}
                <div className="mt-4 text-center">
                  <Link
                    to="/faq"
                    className="text-sm text-[color:var(--pws-plum)] hover:underline"
                  >
                    Questions about donations? See FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <section className="py-8 bg-[color:var(--pws-cream)] border-t border-[color:var(--pws-line)]">
        <div className="container-pws text-center">
          <p className="text-[color:var(--pws-stone)]">
            Panties Without Strings is a campaign of{" "}
            <strong>Safe Work Coalition</strong>, a worker-led organization
            advocating for safety, health, and rights in the adult industry.
          </p>
        </div>
      </section>
    </div>
  );
}
